import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Activity, BarChart3, Globe2, MonitorSmartphone, RefreshCcw, Route } from "lucide-react";

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rsi-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{label}</div>
        <div className="h-10 w-10 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{value}</div>
    </div>
  );
}

function MiniList({ title, items, empty = "No data yet." }) {
  return (
    <div className="rsi-card p-6">
      <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">{title}</h3>
      <div className="mt-4 space-y-3">
        {items?.length ? items.map((item) => (
          <div key={`${title}-${item.label}`} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 px-4 py-3">
            <div className="text-sm text-gray-700 truncate">{item.label}</div>
            <div className="font-semibold text-[var(--rsi-navy)]">{item.count}</div>
          </div>
        )) : <div className="text-sm text-gray-500">{empty}</div>}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-analytics-summary", days],
    queryFn: async () => (await api.get(`/admin/analytics/summary?days=${days}`)).data,
  });

  const maxVisits = Math.max(1, ...(data?.daily_visits || []).map((item) => item.visits || 0));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="badge-purple">
            <BarChart3 className="h-3.5 w-3.5" /> Real Visitor Analytics
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Website Traffic
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Tracks real public page visits reaching the website and shows origin signals, referrers, devices, and top pages.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button type="button" className="btn-ghost" onClick={() => refetch()}>
            <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Visits" value={isLoading ? "—" : data?.totals?.visits ?? 0} icon={Activity} />
        <StatCard label="Unique Sessions" value={isLoading ? "—" : data?.totals?.unique_sessions ?? 0} icon={Route} />
        <StatCard label="Unique Visitors" value={isLoading ? "—" : data?.totals?.unique_visitors ?? 0} icon={Globe2} />
        <StatCard label="Tracked Period" value={`${days}d`} icon={MonitorSmartphone} />
      </div>

      <div className="grid xl:grid-cols-12 gap-6">
        <div className="xl:col-span-7 space-y-6">
          <div className="rsi-card p-6">
            <h2 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
              Daily visits
            </h2>
            <div className="mt-6 space-y-3">
              {data?.daily_visits?.length ? data.daily_visits.map((item) => (
                <div key={item.date}>
                  <div className="flex items-center justify-between gap-3 text-sm mb-1.5">
                    <span className="text-gray-600">{item.date}</span>
                    <span className="font-semibold text-[var(--rsi-navy)]">
                      {item.visits} visits · {item.unique_visitors} visitors
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-[var(--rsi-bg-subtle)] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[var(--rsi-purple)] to-[var(--rsi-navy)]"
                      style={{ width: `${Math.max(6, (item.visits / maxVisits) * 100)}%` }}
                    />
                  </div>
                </div>
              )) : <div className="text-sm text-gray-500">No tracked visits yet. Open the public site to start collecting real traffic data.</div>}
            </div>
          </div>

          <div className="rsi-card p-6">
            <h2 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
              Recent visits
            </h2>
            <div className="mt-4 divide-y divide-gray-100">
              {data?.recent_visits?.length ? data.recent_visits.map((item) => (
                <div key={item.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[var(--rsi-navy)] truncate">{item.path}</div>
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {item.country || item.city || item.timezone || item.language || "Unknown origin"} · {item.device_type}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 truncate">
                      {item.referrer_host || "Direct"} · {item.created_at}
                    </div>
                  </div>
                </div>
              )) : <div className="py-8 text-sm text-gray-500">No recent visits yet.</div>}
            </div>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-6">
          <MiniList title="Top Pages" items={data?.top_pages} />
          <MiniList title="Top Referrers" items={data?.top_referrers} />
          <MiniList title="Visitor Origin Signals" items={data?.top_origins} />
          <MiniList title="Device Breakdown" items={data?.device_breakdown} />
        </div>
      </div>
    </div>
  );
}
